import * as React from "react";
import "./DynProvisionPage.scss";
import DynProvisionProgressDlg from "./DynProvisionProgressDlg";
import Api from "./Api";
import PlainDate from "@testout/testout-commerce/models/PlainDate";
import LabelCtl from "./input/LabelCtl";
import DateCtl from "./input/DateCtl";
import LookupCtl from "./input/LookupCtl";
import EmailCtl from "./input/EmailCtl";
import SelectCtl from "./input/SelectCtl";
import CheckCtl from "./input/CheckCtl";
import AddCampusDlg from "./AddCampusDlg";
import { IDynProvision, IDynProvisionAccount } from "@testout/testout-commerce/models/DynProvision";
import { DynOpportunity } from "./DynOpportunity";
import { DynAccount } from "./DynAccount";
import { DynContact } from "./DynContact";
import { PostalAddress } from "@testout/testout-commerce/models/commerce/PostalAddress";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { Email } from "@testout/testout-commerce/models/Email";
import Segment from "@testout/testout-commerce/models/Segment";
import { Product, ProductType, ProductStatus, IProduct } from "@testout/testout-commerce/models/commerce/Product";
import { ContactEmail } from "@testout/testout-commerce/models/ContactEmail";

interface DynProvisionPageProps {
    objectId?: string;
    objectType?: string;
    env?: string;
}

interface DynProvisionPageState {
    provision: IDynProvision;
    products: ListItem[];
    showProgressDlg: boolean;
    addCampusIndex: number;
    errors: string[];
    isLoading: boolean;
    provisionEnabled: boolean;
}

export default class DynProvisionPage extends React.PureComponent<DynProvisionPageProps, DynProvisionPageState> {
    constructor(props: DynProvisionPageProps) {
        super(props);
        this.state = {
            provision: { accountId: "", accounts: [], products: [] },
            products: [],
            showProgressDlg: false,
            addCampusIndex: -1,
            errors: [],
            isLoading: true,
            provisionEnabled: false
        };
    }

    onProductsChange = (value: string) => {
        this.setState({ provision: { ...this.state.provision, products: value.split(",") } });
    }

    onExpirationDateChange = (value?: Date) => {
        this.setState({ provision: { ...this.state.provision, expirationDate: value } });
    }

    onUseInstructorLicenseChange = (value?: boolean) => {
        this.setState({ provision: { ...this.state.provision, useInstructorLicense: value } });
    }

    onDoAccountSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/dynamics/accounts/listitems/${value}?env=${this.props.env || ""}`)]
            : await Api.get<ListItem[]>(`/api/dynamics/accounts/listitems?search=${value}&env=${this.props.env || ""}`);
    }

    onAccountIdChange = (campusIndex: number, value?: string, data?: {[key: string]: string|undefined}) => {
        const accounts = [...this.state.provision.accounts];
        accounts[campusIndex] = { ...accounts[campusIndex], accountId: value ?? "", 
            customerNumber: data?.["customerNumber"], 
            campusProfileId: data?.["campusProfileId"],
            name: data?.["name"],
            postal: JSON.parse((data?.["postal"] ?? "") as string)
        };
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    onAddCampusDlgOpen = (campusIndex: number) => {
        this.setState({ addCampusIndex: campusIndex });
    }

    onAddCampusDlgClose = (value?: string) => {
        const campusIndex = this.state.addCampusIndex;
        this.setState({ addCampusIndex: -1 }, () => {
            if (value) {
                const accounts = [...this.state.provision.accounts];
                accounts[campusIndex] = { ...accounts[campusIndex], campusProfileId: value ?? "" };
                this.setState({ provision: { ...this.state.provision, accounts: accounts } });
            }
        });
    }

    onDoCampusSearch = async (value: string, exact?: boolean): Promise<ListItem[]> => {
        return exact
            ? [await Api.get<ListItem>(`/api/campuses/${value}/list-item`)]
            : await Api.get<ListItem[]>(`/api/campuses/list-items?search=${value}`);
    }

    onCampusProfileIdChange = (campusIndex: number, value?: string) => {
        const accounts = [...this.state.provision.accounts];
        accounts[campusIndex] = { ...accounts[campusIndex], campusProfileId: value ?? "" };
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    private _abort?: AbortController;
    onCustomerEmailsSelect = async (accountIndex: number, value: string): Promise<ContactEmail[]|null> => {
        // Abort any in progress api call for email addresses
        if (this._abort) this._abort.abort();
        this._abort = new AbortController();
        const accountId = this.state.provision.accounts[accountIndex].accountId;
        try {
            return await Api.get(`/api/dynamics/contacts/emails?search=${value}${accountId ? `&accountId=${accountId}` : ""}&env=${this.props.env || ""}`, this._abort.signal);
        } catch (error: any) {
            if (error.name === "AbortError")
                return null;
            else
                throw error;
        }
    }

    onCustomerEmailsChange = (campusIndex: number, value?: string) => {
        const accounts = [...this.state.provision.accounts];
        accounts[campusIndex] = { ...accounts[campusIndex], emails: value ?? "" };
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    onMakeTeachersAdminsChange = (campusIndex: number, value?: boolean) => {
        const accounts = [...this.state.provision.accounts];
        accounts[campusIndex] = { ...accounts[campusIndex], makeTeachersAdmins: value ?? true };
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    onAddCustomer = (value: string, data?: {[key: string]: string|undefined}) => {
        const accounts = [...this.state.provision.accounts];
        accounts.push({ accountId: value, 
            customerNumber: data?.["customerNumber"], 
            campusProfileId: data?.["campusProfileId"], 
            name: data?.["name"],
            postal: JSON.parse(data?.["postal"] ?? "" as string),
            emails: "" 
        });
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    onRemoveCustomer = (campusIndex: number) => {
        const accounts = [...this.state.provision.accounts];
        accounts.splice(campusIndex, 1);
        this.setState({ provision: { ...this.state.provision, accounts: accounts } });
    }

    onProvisionClick = () => {
        const { provision } = this.state;
        const errors: string[] = [];
        if (!provision.expirationDate || provision.expirationDate < PlainDate.today)
            errors.push("Enter an expiration date that is in the future.");
        if (provision.accounts.length === 0)
            errors.push("Add at least one account to provision.");
        if (provision.products.length === 0)
            errors.push("Add at least one product to provision.");
        for (const account of provision.accounts) {
            if (!account.emails)
                errors.push(`Enter at least one email for ${account.name}.`);
            if (!account.campusProfileId)
                errors.push(`Select or add a campus for ${account.name}`);
        }

        if (errors.length === 0)
            this.setState({ showProgressDlg: true, errors: [] });
        else
            this.setState({ errors: errors });
    }

    onProvisionClose = (failed: boolean) => {
        this.setState({ showProgressDlg: false }, () => {
            if (!failed)
                window.parent.postMessage(JSON.stringify({action: "DONE"}), "*");
        });
    }

    onCancelClick = () => {
        window.parent.postMessage(JSON.stringify({action: "CANCEL"}), "*");
    }

    async loadProducts() {
        try {
            const result = await Api.get<Product[]>("/api/products");
            this.setState({
                products: result
                    .map((product: IProduct) => ({
                        ...product,
                        status: product.status || ProductStatus.Active,
                        productType: product.productType || ProductType.DigitalGoods,
                        culture: product.culture || "en-US"
                    }))
                    .filter((product: IProduct) =>
                        product.status === ProductStatus.Active &&
                        product.productType === ProductType.DigitalGoods &&
                        product.unitType === "License" &&
                        !!product.courseId)
                    .sort((a, b) => a.culture !== "en-US" && b.culture === "en-US" ? 1 : a.culture === "en-US" && b.culture !== "en-US" ? -1 : a.title > b.title ? 1 : a.title < b.title ? -1 : 0)
                    .map((product: IProduct) => ({
                        value: product.productNumber,
                        name: product.title,
                        data: { courseId: product.courseId?.toString(), familyCode: product.familyCode }
                    }))
            });
        }
        catch (error: any) {
            this.setState({ errors: [...this.state.errors, `The list of products failed to load. ${error.message}`] });
        }
    }

    async loadProvision() {
        try {
            let expirationDate: Date;
            let provision: IDynProvision;
            let isSecondaryEd: boolean;
            switch (this.props.objectType) {
                case "Opportunity":
                    const opportunity = await Api.get<DynOpportunity>(`/api/dynamics/opportunities/${this.props.objectId}?getContacts=true&env=${this.props.env || ""}`);
                    if (!opportunity.account)
                        throw new Error("Ensure the opportunity is associated with an account before provisioning.");

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = opportunity.account.channel === "Academic K-12";
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 9, 1)
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    provision = {
                        accountId: opportunity.account.accountid,
                        customerNumber: opportunity.account.accountnumber,
                        campusProfileId: opportunity.account.comptia_labsimcampusprofileid,
                        accounts: [{ 
                            name: opportunity.account.name, 
                            postal: {
                                line1: opportunity.account.address1_line1,
                                line2: opportunity.account.address1_line2,
                                city: opportunity.account.address1_city,
                                stateOrProvince: opportunity.account.address1_stateorprovince,
                                postalCode: opportunity.account.address1_postalcode,
                                countryCode: opportunity.account.address1_country ?? "USA"
                            },
                            accountId: opportunity.account.accountid, 
                            customerNumber: opportunity.account.accountnumber,
                            campusProfileId: opportunity.account.comptia_labsimcampusprofileid,
                            emails: opportunity.contacts.map(contact => new Email(contact.emailaddress1, contact.firstname + " " + contact.lastname).toString()).join(","),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(opportunity.products.map(item => item.productnumber ?? "").filter(productnumber => (this.state.products.find(item => item.value === productnumber)) ))],
                        expirationDate: expirationDate,
                        useInstructorLicense: true,
                        opportunityId: opportunity.opportunityid
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                case "Account":
                    const account = await Api.get<DynAccount>(`/api/dynamics/accounts/${this.props.objectId}?env=${this.props.env || ""}`);

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = account.channel === "Academic K-12" || false;
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 9, 1)
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    // Intialize the list of products to provision
                    const account_prospective_products = "" //TODO: Where should we get this list? (account.prospective_products)
                    const prospectiveFamilyCodes: string[] = (account_prospective_products || "").split(",");
                    const prospectiveProductNumbers: string[] = this.state.products
                        .filter(product => prospectiveFamilyCodes.indexOf(String(product.data?.["familyCode"])) >= 0)
                        .map(product => product.value || "");

                    provision = {
                        accountId: account.accountid,
                        customerNumber: account.accountnumber,
                        campusProfileId: account.comptia_labsimcampusprofileid,
                        accounts: [{ 
                            name: account.name, 
                            postal: {
                                line1: account.address1_line1,
                                line2: account.address1_line2,
                                city: account.address1_city,
                                stateOrProvince: account.address1_stateorprovince,
                                postalCode: account.address1_postalcode,
                                countryCode: account.address1_country ?? "USA"
                            },
                            accountId: account.accountid,
                            customerNumber: account.accountnumber,
                            campusProfileId: account.comptia_labsimcampusprofileid,
                            emails: account.contacts.map(contact => new Email(contact.emailaddress1, contact.firstname + " " + contact.lastname).toString()).join(","),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(prospectiveProductNumbers)],
                        expirationDate: expirationDate,
                        useInstructorLicense: true
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                case "Contact":
                    const contact = await Api.get<DynContact>(`/api/dynamics/contacts/${this.props.objectId}?env=${this.props.env || ""}`);
                    if (!contact.parentcustomerid)
                        throw new Error("Ensure the contact is associated with an account before provisioning.");
                    const contactAccount = await Api.get<DynAccount>(`/api/dynamics/accounts/${contact.parentcustomerid}?env=${this.props.env || ""}`);

                    // Expire at end of academic year (Sep 30) for SecondaryEd or 12 months for HigherEd
                    isSecondaryEd = contactAccount.channel === "Academic K-12" || false;
                    expirationDate = isSecondaryEd
                        ? new Date(PlainDate.today.getFullYear() + (PlainDate.today.getMonth() > 2 ? 1 : 0), 9, 1)
                        : new Date(PlainDate.today.getFullYear() + 1, PlainDate.today.getMonth() + 1, 1);

                    // Initialize the list of products to provision
                    const contact_prospective_products = "" //TODO: Where should we get this? (contact.prospective_products)
                    const prospectiveFamilyCodes2: string[] = (contact_prospective_products || "").split(",");
                    const prospectiveProductNumbers2: string[] = this.state.products
                        .filter(product => prospectiveFamilyCodes2.indexOf(String(product.data?.["familyCode"])) >= 0)
                        .map(product => product.value || "");

                    provision = {
                        accountId: contactAccount.accountid,
                        customerNumber: contactAccount.accountnumber,
                        campusProfileId: contactAccount.comptia_labsimcampusprofileid,
                        accounts: [{ 
                            name: contactAccount.name, 
                            postal: {
                                line1: contactAccount.address1_line1,
                                line2: contactAccount.address1_line2,
                                city: contactAccount.address1_city,
                                stateOrProvince: contactAccount.address1_stateorprovince,
                                postalCode: contactAccount.address1_postalcode,
                                countryCode: contactAccount.address1_country ?? "USA"
                            },
                            accountId: contactAccount.accountid,
                            customerNumber: contactAccount.accountnumber,
                            campusProfileId: contactAccount.comptia_labsimcampusprofileid,
                            emails: new Email(contact.emailaddress1, contact.firstname + " " + contact.lastname).toString(),
                            makeTeachersAdmins: isSecondaryEd
                        }],
                        products: [...new Set(prospectiveProductNumbers2)],
                        expirationDate: expirationDate,
                        useInstructorLicense: true
                    };
                    this.setState({ provision: provision, isLoading: false, provisionEnabled: true });
                    break;
                default:
                    this.setState({ errors: [...this.state.errors, `Provision from ${this.props.objectType} is not supported.`] });
                    break;
            }
        }
        catch (error: any) {
            console.log(`exception ${error}`);
            this.setState({ errors: [...this.state.errors, `The ${this.props.objectType} properties failed to load. ${error.message}`] });
        }
    }

    async componentDidMount() {
        try {
            await this.loadProducts();
            await this.loadProvision();
        }
        finally {
            this.setState({ isLoading: false });
        }
    }

    render() {
        const { provision, addCampusIndex } = this.state;

        return this.state.isLoading
            ? <div className="loading">
                  <div>Loading...
                      <div className="spinner"></div></div></div>
            : <div className="page ProvisionPage">

                  <header>
                      <p>Select the CertMaster products and expiration date, and enter instructor email addresses to provision.
                          Each instructor will be given access to the products and sent a welcome email with login credentials.</p>
                      {this.state.errors.length > 0 &&
                          <p className="error">{this.state.errors.join(" ")}</p>}
                  </header>

                  <main>
                      <section className="common">
                          <LabelCtl label="Products">
                              <SelectCtl placeholder="Select products" multiSelect={true} hideItemValue={true}
                                         value={provision.products.join(",")} onChange={this.onProductsChange}
                                         items={this.state.products}/>
                          </LabelCtl>
                          <LabelCtl label="Expiration Date">
                              <DateCtl placeholder="MM/DD/YYYY" value={provision.expirationDate} onChange={this
                                  .onExpirationDateChange}/>
                              <CheckCtl label="Use Instructor License"
                                        value={provision.useInstructorLicense}
                                        onChange={this.onUseInstructorLicenseChange}/>
                          </LabelCtl>
                      </section>

                      <section className="teachers">
                          <h3>Teachers</h3>
                          <table>
                              <thead>
                              <tr>
                                  <th className="item-delete">&nbsp;</th>
                                  <th className="item-campus">Account</th>
                                  <th className="item-emails">Name &lt;Email&gt; List</th>
                              </tr>
                              </thead>
                              <tbody>

                              {provision.accounts.map((campus: IDynProvisionAccount, i) =>
                                  <tr key={i}>
                                      <td>
                                          <button className="trash"
                                                  onClick={() => this.onRemoveCustomer(i)}>&nbsp;</button></td>
                                      <td>
                                          <LookupCtl placeholder="Account"
                                                     onSearch={this.onDoAccountSearch} hideItemValue={true}
                                                     value={campus.accountId}
                                                     onChange={(value, data) => this.onAccountIdChange(i, value, data)}/>
                                          <LabelCtl label="Institution" className="campus" useDiv={true}>
                                              <button className="link"
                                                      onClick={() => this.onAddCampusDlgOpen(i)}>+ Add</button>
                                              <LookupCtl placeholder="Institution"
                                                         onSearch={this.onDoCampusSearch} hideItemValue={true}
                                                         value={campus.campusProfileId}
                                                         onChange={(value) => this.onCampusProfileIdChange(i, value)}/>
                                          </LabelCtl>
                                      </td>
                                      <td>
                                          <EmailCtl placeholder="Email List" value={campus.emails}
                                                    onChange={(value) => this.onCustomerEmailsChange(i, value)}
                                                    onSelect={(value) => this.onCustomerEmailsSelect(i, value)}/>
                                          <CheckCtl label="Make these instructors institution administrators"
                                                    value={campus.makeTeachersAdmins}
                                                    onChange={(value) => this.onMakeTeachersAdminsChange(i, value)}/>
                                      </td>
                                  </tr>)}

                              <tr>
                                  <td>&nbsp;</td>
                                  <td>
                                      <LookupCtl key="_add_" placeholder="Add account"
                                                 onSearch={this.onDoAccountSearch} onChange={this.onAddCustomer}
                                                 hideItemValue={true}/></td>
                                  <td></td>
                              </tr>
                              </tbody>
                          </table>
                      </section>
                  </main>

                  <footer>
                      <button className="secondary" onClick={this.onCancelClick}>Cancel</button>
                      <button className="primary" onClick={this.onProvisionClick}
                              disabled={!this.state.provisionEnabled}>Provision</button>
                  </footer>

                <DynProvisionProgressDlg provision={this.state.provision} products={this.state.products} env={this.props.env}
                                        isOpen={this.state.showProgressDlg} onClose={this.onProvisionClose}/>
                  <AddCampusDlg defaultName={addCampusIndex >= 0
                      ? provision.accounts[addCampusIndex].name
                      : undefined}
                                defaultPostal={addCampusIndex >= 0
                                    ? new PostalAddress(provision.accounts[addCampusIndex].postal)
                                    : undefined}
                                isOpen={addCampusIndex >= 0} onClose={this.onAddCampusDlgClose}/>

              </div>;
    }
}